import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PreconditionNodeData } from "@/lib/productGraph";
import type {
  PreconditionFieldDef,
  PreconditionRule,
  PreconditionRuleConditional,
  PreconditionRuleMaxPercentage,
} from "@/services/platformEnterprisesService";

import { PreconditionRuleConditionalEditor } from "./precondition-rule-conditional";

interface Props {
  data: PreconditionNodeData;
  onChange: (patch: Partial<PreconditionNodeData>) => void;
}

const FIELD_TYPES: PreconditionFieldDef["type"][] = ["number", "currency", "select", "text"];

function emptyField(): PreconditionFieldDef {
  return { key: "", label: "", type: "number" };
}

function emptyConditionalRule(): PreconditionRuleConditional {
  return {
    type: "conditional",
    conditions: [
      {
        left: { kind: "field", key: "" },
        op: "gte",
        right: { kind: "const", value: 0 },
      },
    ],
  };
}

export function PreconditionEditor({ data, onChange }: Props): JSX.Element {
  const { t } = useTranslation();

  const fields = data.fields ?? [];
  const rules = data.rules ?? [];

  function updateField(idx: number, patch: Partial<PreconditionFieldDef>): void {
    onChange({
      fields: fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    });
  }

  function removeField(idx: number): void {
    onChange({ fields: fields.filter((_, i) => i !== idx) });
  }

  function addField(): void {
    onChange({ fields: [...fields, emptyField()] });
  }

  function addRule(): void {
    onChange({ rules: [...rules, emptyConditionalRule()] });
  }

  function updateRule(idx: number, rule: PreconditionRule): void {
    onChange({ rules: rules.map((r, i) => (i === idx ? rule : r)) });
  }

  function removeRule(idx: number): void {
    onChange({ rules: rules.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">{t("enterpriseProductBuilder.nodeLabelLabel")}</Label>
        <Input
          value={data.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.nodeDescriptionLabel")}
        </Label>
        <Textarea
          value={data.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.preconditionPassMsg")}
          </Label>
          <Textarea
            value={data.passMessage}
            onChange={(e) => onChange({ passMessage: e.target.value })}
            rows={2}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.preconditionFailMsg")}
          </Label>
          <Textarea
            value={data.failMessage}
            onChange={(e) => onChange({ failMessage: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("enterpriseProductBuilder.preconditionFieldsLabel")}
          </Label>
          <Button size="sm" variant="outline" onClick={addField}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("enterpriseProductBuilder.addField")}
          </Button>
        </div>
        {fields.length === 0 && (
          <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
            {t("enterpriseProductBuilder.noFieldsYet")}
          </p>
        )}
        {fields.map((f, idx) => (
          <div key={idx} className="space-y-2 rounded-md border bg-muted/30 p-3">
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={f.key}
                onChange={(e) => updateField(idx, { key: e.target.value })}
                placeholder="key"
                className="h-8 text-xs"
              />
              <Select
                value={f.type}
                onValueChange={(v) =>
                  updateField(idx, { type: v as PreconditionFieldDef["type"] })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((tp) => (
                    <SelectItem key={tp} value={tp}>
                      {t(`enterpriseProductBuilder.fieldTypeLabel.${tp}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input
              value={f.label}
              onChange={(e) => updateField(idx, { label: e.target.value })}
              placeholder={t("enterpriseProductBuilder.fieldLabel")}
              className="h-8 text-xs"
            />
            <Input
              value={f.hint ?? ""}
              onChange={(e) => updateField(idx, { hint: e.target.value || undefined })}
              placeholder="Texto de ayuda (opcional)"
              className="h-7 text-xs text-muted-foreground"
            />
            {(f.type === "number" || f.type === "currency") && (
              <div className="flex items-center gap-1.5">
                <Label className="shrink-0 text-[10px] text-muted-foreground">Máx %</Label>
                <Select
                  value={f.maxPercentHint?.field ?? ""}
                  onValueChange={(v) =>
                    updateField(idx, {
                      maxPercentHint: v
                        ? { field: v, percent: f.maxPercentHint?.percent ?? 35 }
                        : undefined,
                    })
                  }
                >
                  <SelectTrigger className="h-7 flex-1 text-xs">
                    <SelectValue placeholder="Campo ref." />
                  </SelectTrigger>
                  <SelectContent>
                    {fields
                      .filter((other) => other.key !== f.key && (other.type === "number" || other.type === "currency"))
                      .map((other) => (
                        <SelectItem key={other.key} value={other.key}>
                          {other.label || other.key}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {f.maxPercentHint && (
                  <Input
                    type="number"
                    value={f.maxPercentHint.percent}
                    onChange={(e) =>
                      updateField(idx, {
                        maxPercentHint: { ...f.maxPercentHint!, percent: Number(e.target.value) || 0 },
                      })
                    }
                    className="h-7 w-16 text-xs"
                    min={1}
                    max={100}
                  />
                )}
                {f.maxPercentHint && (
                  <span className="text-[10px] text-muted-foreground">%</span>
                )}
              </div>
            )}
            {f.type === "select" && (
              <div className="space-y-1.5">
                <Label className="text-xs">
                  {t("enterpriseProductBuilder.fieldOptions")}
                </Label>
                <div className="space-y-1.5">
                  {(f.options ?? []).map((opt, optIdx) => (
                    <div key={optIdx} className="flex items-center gap-1.5">
                      <Input
                        value={opt.value}
                        onChange={(e) => {
                          const next = [...(f.options ?? [])];
                          next[optIdx] = { ...opt, value: e.target.value };
                          updateField(idx, { options: next });
                        }}
                        placeholder={t("enterpriseProductBuilder.optionValue")}
                        className="h-7 flex-1 text-xs"
                      />
                      <Input
                        value={opt.label}
                        onChange={(e) => {
                          const next = [...(f.options ?? [])];
                          next[optIdx] = { ...opt, label: e.target.value };
                          updateField(idx, { options: next });
                        }}
                        placeholder={t("enterpriseProductBuilder.optionLabel")}
                        className="h-7 flex-1 text-xs"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          const next = (f.options ?? []).filter((_, i) => i !== optIdx);
                          updateField(idx, { options: next });
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 w-full text-xs"
                    onClick={() => {
                      const next = [...(f.options ?? []), { value: "", label: "" }];
                      updateField(idx, { options: next });
                    }}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    {t("enterpriseProductBuilder.addOption")}
                  </Button>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id={`field-${idx}-perActor`}
                  checked={f.perActor ?? false}
                  onCheckedChange={(checked) =>
                    updateField(idx, { ...f, perActor: checked === true })
                  }
                />
                <Label htmlFor={`field-${idx}-perActor`} className="text-xs cursor-pointer text-muted-foreground">
                  Por participante (se repite por cada actor)
                </Label>
              </div>
              <Button
                size="sm"
                variant="ghost-destructive"
                className="h-7"
                onClick={() => removeField(idx)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("enterpriseProductBuilder.preconditionRulesLabel")}
          </Label>
          <Button size="sm" variant="outline" onClick={addRule}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("enterpriseProductBuilder.addRule")}
          </Button>
        </div>
        {rules.length === 0 && (
          <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
            {t("enterpriseProductBuilder.noRulesYet")}
          </p>
        )}
        {rules.map((rule, idx) => {
          if (rule.type === "conditional") {
            return (
              <PreconditionRuleConditionalEditor
                key={idx}
                rule={rule}
                fields={fields}
                onChange={(next) => updateRule(idx, next)}
                onRemove={() => removeRule(idx)}
              />
            );
          }
          return (
            <MaxPercentageRuleLegacy
              key={idx}
              rule={rule}
              fields={fields}
              onChange={(next) => updateRule(idx, next)}
              onRemove={() => removeRule(idx)}
            />
          );
        })}
      </div>
    </div>
  );
}

interface LegacyProps {
  rule: PreconditionRuleMaxPercentage;
  fields: PreconditionFieldDef[];
  onChange: (next: PreconditionRuleMaxPercentage) => void;
  onRemove: () => void;
}

function MaxPercentageRuleLegacy({
  rule,
  fields,
  onChange,
  onRemove,
}: LegacyProps): JSX.Element {
  const { t } = useTranslation();
  const numberFields = fields.filter(
    (f) => (f.type === "number" || f.type === "currency") && f.key.trim() !== "",
  );
  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-muted-foreground">
          {t("enterpriseProductBuilder.ruleMaxPercentage")}{" "}
          <span className="ml-1 rounded bg-muted px-1 py-0.5 text-[10px]">
            {t("enterpriseProductBuilder.ruleLegacyBadge")}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost-destructive"
          className="h-7"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.ruleSumFields")}
        </Label>
        <div className="flex flex-wrap gap-1">
          {numberFields.length === 0 ? (
            <span className="text-xs text-muted-foreground">
              {t("enterpriseProductBuilder.addNumberFieldsFirst")}
            </span>
          ) : (
            numberFields.map((f) => {
              const checked = rule.params.sumFields.includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() =>
                    onChange({
                      ...rule,
                      params: {
                        ...rule.params,
                        sumFields: checked
                          ? rule.params.sumFields.filter((k) => k !== f.key)
                          : [...rule.params.sumFields, f.key],
                      },
                    })
                  }
                  className={
                    "rounded-md border px-2 py-0.5 text-xs transition-colors " +
                    (checked
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input bg-background")
                  }
                >
                  {f.label || f.key}
                </button>
              );
            })
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.ruleReferenceField")}
          </Label>
          <Select
            value={rule.params.referenceField || undefined}
            onValueChange={(v) =>
              onChange({ ...rule, params: { ...rule.params, referenceField: v } })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {numberFields.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.label || f.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.ruleMaxPct")}
          </Label>
          <Input
            type="number"
            min={0}
            max={100}
            value={rule.params.maxPercentage}
            onChange={(e) =>
              onChange({
                ...rule,
                params: {
                  ...rule.params,
                  maxPercentage: Number(e.target.value),
                },
              })
            }
            className="h-8 text-xs"
          />
        </div>
      </div>
    </div>
  );
}
